import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FileText, Loader2, DollarSign, CheckCircle2, Clock, Ban,
  Trash2, Receipt, ChevronRight, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useConfirm } from "../hooks/useConfirm";
import { useI18n } from "../i18n/I18nContext";

const fmtUSD = (v) => `$${Number(v || 0).toFixed(2)}`;
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
}

const INV_STATUS = {
  unpaid: { label: "Em aberto", Icon: Clock, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  paid: { label: "Paga", Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  void: { label: "Cancelada", Icon: Ban, cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const PAYMENT_METHODS = ["cash", "zelle", "venmo", "stripe", "other"];

export default function Invoices() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [tab, setTab] = useState("pending");
  const [summary, setSummary] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genClient, setGenClient] = useState(null); // client row for generate dialog
  const [settleInv, setSettleInv] = useState(null); // invoice for settle dialog

  async function load() {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([
        api.get("/invoices/clients-summary"),
        api.get("/invoices"),
      ]);
      setSummary(s.data || []);
      setInvoices(i.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    pendingTotal: summary.reduce((a, r) => a + Number(r.pending_total || 0), 0),
    pendingClients: summary.length,
    openInvoices: invoices.filter((i) => i.status === "unpaid"),
    openTotal: invoices.filter((i) => i.status === "unpaid").reduce((a, i) => a + Number(i.total || 0), 0),
  }), [summary, invoices]);

  async function deleteInvoice(inv) {
    const ok = await confirm({
      title: t("Excluir fatura?"),
      description: t("Os itens voltarão para 'pendente' e poderão ser faturados novamente."),
      destructive: true,
      actionLabel: t("Excluir"),
    });
    if (!ok) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(t("Fatura excluída"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="invoices-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="text-blue-600" /> {t("A Receber")}
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            {t("Cada casa finalizada vira um item. Junte os itens de um cliente em uma fatura única para uma só cobrança.")}
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <KpiCard label={t("Itens pendentes (total)")} value={fmtUSD(totals.pendingTotal)} sub={`${totals.pendingClients} ${t("cliente(s)")}`} tone="amber" testId="kpi-pending" />
          <KpiCard label={t("Faturas em aberto")} value={fmtUSD(totals.openTotal)} sub={`${totals.openInvoices.length} ${t("fatura(s)")}`} tone="blue" testId="kpi-open" />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="invoices-tabs">
            <TabsTrigger value="pending" data-testid="tab-pending">{t("Pendentes por cliente")}</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">{t("Faturas")}</TabsTrigger>
          </TabsList>

          {/* ---- Pendentes por cliente ---- */}
          <TabsContent value="pending" className="mt-4">
            {loading ? (
              <Loading />
            ) : summary.length === 0 ? (
              <Empty text={t("Nenhum item pendente. Finalize casas para gerar itens de fatura.")} />
            ) : (
              <ul className="space-y-2.5" data-testid="pending-clients-list">
                {summary.map((r) => (
                  <li
                    key={r.client_id}
                    data-testid={`pending-client-${r.client_id}`}
                    className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{r.client_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{r.pending_count} {t("item(ns)")}</span>
                        <span>•</span>
                        <span className="inline-flex items-center gap-1"><Calendar size={11} /> {t("desde")} {fmtDate(r.oldest_date)}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold text-slate-900">{fmtUSD(r.pending_total)}</div>
                    </div>
                    <Button
                      data-testid={`generate-invoice-${r.client_id}`}
                      onClick={() => setGenClient(r)}
                      className="flex-shrink-0 bg-blue-600 hover:bg-blue-700"
                    >
                      <Receipt size={15} className="mr-1.5" /> {t("Gerar fatura")}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          {/* ---- Faturas consolidadas ---- */}
          <TabsContent value="invoices" className="mt-4">
            {loading ? (
              <Loading />
            ) : invoices.length === 0 ? (
              <Empty text={t("Nenhuma fatura gerada ainda.")} />
            ) : (
              <ul className="space-y-2.5" data-testid="invoices-list">
                {invoices.map((inv) => {
                  const st = INV_STATUS[inv.status] || INV_STATUS.unpaid;
                  const StIcon = st.Icon;
                  return (
                    <li
                      key={inv.id}
                      data-testid={`invoice-${inv.id}`}
                      className="bg-white rounded-xl border border-slate-200 p-4"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                            {t("Fatura")} #{inv.number} — {inv.client_name}
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${st.cls}`}>
                              <StIcon size={11} /> {t(st.label)}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {(inv.items || []).length} {t("item(ns)")} • {fmtDate(inv.period_from)} → {fmtDate(inv.period_to)}
                          </div>
                          {inv.status === "paid" && (
                            <div className="text-xs text-emerald-700 mt-0.5">
                              {t("Baixa em")} {fmtDate(inv.paid_at)}{inv.payment_method ? ` • ${inv.payment_method}` : ""}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-slate-900">{fmtUSD(inv.total)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {inv.status === "unpaid" && (
                          <Button
                            data-testid={`settle-invoice-${inv.id}`}
                            onClick={() => setSettleInv(inv)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <DollarSign size={15} className="mr-1" /> {t("Dar baixa")}
                          </Button>
                        )}
                        <Button
                          data-testid={`delete-invoice-${inv.id}`}
                          onClick={() => deleteInvoice(inv)}
                          variant="outline"
                          className="text-rose-700 border-rose-200 hover:bg-rose-50"
                        >
                          <Trash2 size={15} className="mr-1" /> {t("Excluir")}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {genClient && (
        <GenerateInvoiceDialog
          client={genClient}
          onClose={() => setGenClient(null)}
          onDone={async () => { setGenClient(null); await load(); setTab("invoices"); }}
        />
      )}
      {settleInv && (
        <SettleDialog
          invoice={settleInv}
          onClose={() => setSettleInv(null)}
          onDone={async () => { setSettleInv(null); await load(); }}
        />
      )}
    </AdminLayout>
  );
}

function KpiCard({ label, value, sub, tone, testId }) {
  const tones = {
    amber: "from-amber-50 to-orange-50 border-amber-200 text-amber-900",
    blue: "from-blue-50 to-indigo-50 border-blue-200 text-blue-900",
  };
  return (
    <div data-testid={testId} className={`rounded-xl border bg-gradient-to-br ${tones[tone]} p-4`}>
      <div className="text-xs uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      <div className="text-xs opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}

const Loading = () => (
  <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carregando…</div>
);
const Empty = ({ text }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="invoices-empty">{text}</div>
);

function GenerateInvoiceDialog({ client, onClose, onDone }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("all"); // all | period
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ client_id: client.client_id, status: "pending" });
      if (mode === "period") {
        if (from) params.append("date_from", from);
        if (to) params.append("date_to", to);
      }
      const { data } = await api.get(`/invoices/items?${params.toString()}`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [client.client_id, mode, from, to]);
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const total = items.reduce((a, i) => a + Number(i.amount || 0), 0);

  async function generate() {
    if (items.length === 0) { toast.error(t("Nenhum item no período selecionado")); return; }
    setSaving(true);
    try {
      const payload = { client_id: client.client_id };
      if (mode === "period") {
        if (from) payload.date_from = from;
        if (to) payload.date_to = to;
      }
      const { data } = await api.post("/invoices", payload);
      toast.success(t("Fatura #{n} gerada — {v}").replace("{n}", data.number).replace("{v}", fmtUSD(data.total)));
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha ao gerar fatura"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="generate-invoice-dialog">
        <DialogHeader>
          <DialogTitle>{t("Gerar fatura")} — {client.client_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              data-testid="gen-mode-all"
              onClick={() => setMode("all")}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition ${mode === "all" ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
            >
              {t("Juntar tudo")}
            </button>
            <button
              data-testid="gen-mode-period"
              onClick={() => setMode("period")}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition ${mode === "period" ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
            >
              {t("Escolher período")}
            </button>
          </div>

          {mode === "period" && (
            <div className="grid grid-cols-2 gap-3" data-testid="gen-period-fields">
              <div>
                <label className="text-xs font-semibold text-slate-600">{t("De")}</label>
                <DatePickerUS value={from} onChange={setFrom} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">{t("Até")}</label>
                <DatePickerUS value={to} onChange={setTo} />
              </div>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50 max-h-52 overflow-y-auto" data-testid="gen-items-preview">
            {loading ? (
              <div className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carregando…</div>
            ) : items.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 text-center">{t("Nenhum item pendente neste período.")}</div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {items.map((it) => (
                  <li key={it.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 text-slate-700">
                      <ChevronRight size={13} className="text-slate-400" />
                      {it.description || "Limpeza"} • {fmtDate(it.service_date)}
                    </span>
                    <span className="font-semibold text-slate-900">{fmtUSD(it.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg bg-blue-600 text-white px-4 py-3">
            <span className="font-semibold">{t("Total da fatura")} ({items.length} {t("item(ns)")})</span>
            <span className="text-xl font-bold" data-testid="gen-total">{fmtUSD(total)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button
            data-testid="gen-confirm"
            onClick={generate}
            disabled={saving || items.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : <Receipt size={16} className="mr-1" />}
            {t("Gerar fatura")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettleDialog({ invoice, onClose, onDone }) {
  const { t } = useI18n();
  const [method, setMethod] = useState("cash");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  async function settle() {
    setSaving(true);
    try {
      await api.post(`/invoices/${invoice.id}/settle`, { method, paid_at: paidAt, notes });
      toast.success(t("Baixa registrada"));
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha ao dar baixa"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="settle-dialog">
        <DialogHeader>
          <DialogTitle>{t("Dar baixa")} — {t("Fatura")} #{invoice.number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-center rounded-lg bg-emerald-50 border border-emerald-200 py-3">
            <div className="text-xs text-emerald-700 uppercase tracking-wider font-semibold">{t("Total")}</div>
            <div className="text-2xl font-bold text-emerald-800">{fmtUSD(invoice.total)}</div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">{t("Forma de pagamento")}</label>
            <div className="grid grid-cols-3 gap-1.5 mt-1" data-testid="settle-methods">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m}
                  data-testid={`settle-method-${m}`}
                  onClick={() => setMethod(m)}
                  className={`rounded-lg border px-2 py-1.5 text-xs font-semibold capitalize transition ${method === m ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">{t("Data do pagamento")}</label>
            <DatePickerUS value={paidAt} onChange={setPaidAt} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">{t("Observações")} ({t("opcional")})</label>
            <textarea
              data-testid="settle-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button data-testid="settle-confirm" onClick={settle} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : <CheckCircle2 size={16} className="mr-1" />}
            {t("Confirmar baixa")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
